import React, { useState, useEffect } from "react"
import { Link } from "gatsby"
import DeurenLogo from "../../images/DeurenLogo"
import useRafScrollState from "src/utils/useRafScrollState"
import Icon from "src/components/Icon"

const isSearchNavItem = item => {
  const label = item.label || ""
  const target = item.target || ""

  return target === "/search/" || /^(find anything|search)$/i.test(label)
}

const SearchIcon = ({ className = "", size = 14 }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox={size === 18 ? "0 0 18 18" : "0 0 14 14"}
    fill="none"
    aria-hidden
  >
    {size === 18 ? (
      <>
        <path
          d="M8.25 14.25C11.5637 14.25 14.25 11.5637 14.25 8.25C14.25 4.93629 11.5637 2.25 8.25 2.25C4.93629 2.25 2.25 4.93629 2.25 8.25C2.25 11.5637 4.93629 14.25 8.25 14.25Z"
          stroke="currentColor"
          strokeLinejoin="round"
        />
        <path
          d="M15.75 15.75L12.4875 12.4875"
          stroke="currentColor"
          strokeLinejoin="round"
        />
      </>
    ) : (
      <>
        <path
          d="M6.41667 11.0833C8.994 11.0833 11.0833 8.994 11.0833 6.41667C11.0833 3.83934 8.994 1.75 6.41667 1.75C3.83934 1.75 1.75 3.83934 1.75 6.41667C1.75 8.994 3.83934 11.0833 6.41667 11.0833Z"
          stroke="currentColor"
          strokeLinejoin="round"
        />
        <path
          d="M12.25 12.25L9.71252 9.71252"
          stroke="currentColor"
          strokeLinejoin="round"
        />
      </>
    )}
  </svg>
)

const SearchNavLabel = ({ label }) => (
  <span className="inline-flex items-center">
    <SearchIcon className="mr-2 text-darkgrey transition duration-300 ease-in-out group-hover:text-gold" />
    {label}
  </span>
)

const NavItem = props => {
  const handleKeyDown = event => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      props.onClick()
    }
  }

  return (
    <div
      className={`group min-h-[44px] px-6 py-4 border-b border-grey-300 uppercase font-bold font-body tracking-wider bg-white static leading-5 focus:outline-none flex items-center`}
      role="button"
      tabIndex={0}
      onClick={props.onClick}
      onKeyDown={handleKeyDown}
    >
      <span className="text-xsmenu">
        {isSearchNavItem(props) ? (
          <SearchNavLabel label={props.label} />
        ) : (
          props.label
        )}
      </span>
      <Icon
        className="fal fa-angle-right text-base text-gold align-middle ml-auto"
        aria-hidden
      />
    </div>
  )
}

const NavSlide = props => {
  const handleKeyDown = event => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      props.onClick()
    }
  }

  return (
    <div
      className={`absolute flex flex-grow float-left flex-col bg-offwhite top-0 w-screen transition-all duration-500 overflow-y-scroll ${
        props.show ? "right-0" : "right-[-100%]"
      }`}
    >
      <span
        className="min-h-[44px] flex items-center px-6 py-4 border-b border-grey-300 text-xsmenu uppercase font-bold tracking-wider font-body text-gold bg-white leading-5 focus:outline-none"
        role="button"
        tabIndex={0}
        onClick={props.onClick}
        onKeyDown={handleKeyDown}
      >
        <Icon
          className="fal fa-angle-left text-base text-gold align-middle mr-2"
          aria-hidden
        />{" "}
        {props.label}
      </span>
      {props.items.map((navItem, index) => {
        return (
          <div
            key={index}
            className={`transform transition-all duration-500 ${
              props.show
                ? "opacity-1 translate-x-0"
                : "opacity-0 -translate-x-3"
            }`}
            style={{
              transitionDelay: `${props.show ? `${index + 4}00ms` : "0ms"}`,
            }}
          >
            <Link
              className="min-h-[44px] flex items-center px-6 py-4 border-b border-grey-300 text-xs font-body normal-case font-normal tracking-normal leading-5 focus:outline-none"
              to={`${navItem.target}`}
              onClick={props.closeParent}
              onKeyDown={props.closeParent}
              key={index}
            >
              {navItem.label}
            </Link>
          </div>
        )
      })}
    </div>
  )
}

const NavigationMobile = props => {
  const [state, setState] = useState(
    props.items
      .filter(item => !isSearchNavItem(item))
      .map(item => {
        return {
          ...item,
          show: false,
        }
      })
  )

  const [show, setShow] = useState(false)
  const [childOpen, setChildOpen] = useState(false)

  const handleClick = () => {
    setShow(!show)
    setChildOpen(false)
    setState(prevState =>
      prevState.map(item => {
        return {
          ...item,
          show: false,
        }
      })
    )
  }

  const updateState = value => {
    setState(prevState => {
      const nextState = prevState.map(item => {
        const nextShow = item.label === value ? !item.show : false
        return {
          ...item,
          show: nextShow,
        }
      })
      setChildOpen(nextState.some(item => item.show))
      return nextState
    })
  }

  const [animate, setAnimate] = useState(true)
  const scrolled = useRafScrollState(({ scrollTop }) => scrollTop > 50)

  useEffect(() => {
    if (scrolled) {
      setAnimate(true)
      return undefined
    }

    const timeoutId = setTimeout(() => {
      setAnimate(false)
    }, 2000)

    return () => clearTimeout(timeoutId)
  }, [scrolled])

  const handleMenuKeyDown = event => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      handleClick()
    }
  }

  return (
    <nav
      className={`z-40 flex relative bg-white border-b border-gray-200 items-center xl:hidden min-h-[64px]`}
    >
      <Link
        className="mr-auto text-darkgrey min-w-[44px] min-h-[44px] flex items-center justify-center relative left-3"
        to="/contact/"
        aria-label="Contact Us"
      >
        <img src="/message.svg" alt="Contact" width="18" height="13" />
      </Link>
      <Link
        className="absolute left-1/2 w-28 min-h-[44px] flex items-center justify-center transform -translate-x-1/2"
        to="/"
        aria-label="Deuren Homepage"
      >
        <DeurenLogo
          className={`w-full overflow-visible ${animate ? "animate" : ""}`}
        />
      </Link>
      <Link
        className="group ml-auto min-w-[44px] min-h-[44px] focus:outline-none flex items-center justify-center"
        to="/search/"
        aria-label="Find Anything"
      >
        <SearchIcon
          size={18}
          className="text-darkgrey transition duration-300 ease-in-out group-hover:text-gold"
        />
      </Link>
      <span
        className="min-w-[44px] min-h-[44px] focus:outline-none flex items-center justify-center relative right-3"
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleMenuKeyDown}
      >
        <span className="hidden">Menu</span>
        <div
          className={`bg-darkgrey h-0.1 w-6 transition-all duration-500 absolute left-1/2 -translate-x-1/2 transform origin-center ${
            show ? "top-1/2 rotate-135" : "top-[40%] rotate-0"
          }`}
        ></div>
        <div
          className={`bg-darkgrey h-0.1 w-6 transition-all duration-500 absolute left-1/2 -translate-x-1/2 transform origin-center ${
            show ? "top-1/2 -rotate-135" : "top-[60%] rotate-0"
          }`}
        ></div>
      </span>
      <div
        className={`fixed flex flex-col w-screen bg-white overflow-y-scroll transition-all duration-500 z-30 ${
          show ? "left-0" : "left-[-100%]"
        }`}
        style={{
          top: "92px",
          minHeight: "calc(100% - 92px)",
          maxHeight: "calc(100% - 92px)",
        }}
      >
        <div
          className={`bg-offwhite overflow-y-scroll overflow-x-hidden flex flex-col flex-1 relative`}
        >
          <div
            className={`absolute flex flex-grow flex-col w-full transition-all duration-500 ${
              childOpen ? "-left-full" : "left-0"
            }`}
          >
            {state.map((navItem, index) => {
              return (
                <React.Fragment key={index}>
                  <div
                    className={`transform transition-all duration-500 ${
                      show && !childOpen
                        ? "opacity-1 translate-x-0"
                        : "opacity-0 -translate-x-3"
                    }`}
                    style={{
                      transitionDelay: `${
                        show && !childOpen ? `${index + 4}00ms` : "0ms"
                      }`,
                    }}
                  >
                    {navItem.items.length ? (
                      <NavItem
                        label={navItem.label}
                        items={navItem.items}
                        scrolled={scrolled}
                        closeParent={handleClick}
                        onClick={() => updateState(navItem.label)}
                        show={navItem.show}
                      />
                    ) : (
                      <Link
                        className="group min-h-[44px] flex items-center px-6 py-4 border-b border-grey-300 text-xsmenu uppercase font-bold font-body tracking-widest bg-white leading-5 focus:outline-none"
                        to={`${navItem.target}`}
                        onClick={handleClick}
                        onKeyDown={handleClick}
                      >
                        {isSearchNavItem(navItem) ? (
                          <SearchNavLabel label={navItem.label} />
                        ) : (
                          navItem.label
                        )}
                      </Link>
                    )}
                  </div>
                </React.Fragment>
              )
            })}
          </div>
          {state.map((navItem, index) => {
            return (
              <React.Fragment key={index}>
                {navItem.items.length > 0 && (
                  <NavSlide
                    label={navItem.label}
                    items={navItem.items}
                    scrolled={scrolled}
                    closeParent={handleClick}
                    onClick={() => updateState(navItem.label)}
                    show={navItem.show}
                  />
                )}
              </React.Fragment>
            )
          })}
        </div>

        <div className="bg-darkgrey p-6">
          <ul className="list-none text-xs text-white mb-4">
            <li className="mb-3">
              <a
                href="/request-a-quote/"
                className="text-white min-h-[44px] flex items-center"
              >
                Request a Quote
              </a>
            </li>
            <li className="mb-3">
              <a
                href="/brochure-downloads/"
                className="text-white min-h-[44px] flex items-center"
              >
                Brochure Downloads
              </a>
            </li>
            <li className="mb-3">
              <a
                href="/architects-designers/"
                className="text-white min-h-[44px] flex items-center"
              >
                Architects and designers
              </a>
            </li>
          </ul>
          <ul className="list-none text-white flex flex-row relative -left-3">
            <li>
              <a
                href="https://www.facebook.com/deurenuk/"
                aria-label="Facebook"
                className="text-white min-w-[44px] min-h-[44px] flex items-center justify-center"
                target="_blank"
                rel="noreferrer"
              >
                <Icon className="fab fa-facebook" aria-hidden />{" "}
              </a>
            </li>
            <li>
              <a
                href="https://www.instagram.com/deurendoors/"
                aria-label="Instagram"
                className="text-white min-w-[44px] min-h-[44px] flex items-center justify-center"
                target="_blank"
                rel="noreferrer"
              >
                <Icon className="fab fa-instagram" aria-hidden />{" "}
              </a>
            </li>
            <li>
              <a
                href="https://twitter.com/DeurenDoors?lang=en-gb"
                aria-label="Twitter"
                className="text-white min-w-[44px] min-h-[44px] flex items-center justify-center"
                target="_blank"
                rel="noreferrer"
              >
                <Icon className="fab fa-twitter" aria-hidden />{" "}
              </a>
            </li>
            <li>
              <a
                href="https://www.pinterest.co.uk/deurendoors/"
                aria-label="Pinterest"
                className="text-white min-w-[44px] min-h-[44px] flex items-center justify-center"
                target="_blank"
                rel="noreferrer"
              >
                <Icon className="fab fa-pinterest" aria-hidden />{" "}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  )
}

export default NavigationMobile
